import { useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCcw } from 'lucide-react'
import type { SourceConflictResolution } from '@agentwiki/shared'
import {
  sourceConflictErrorMessage,
  useResolveSourceConflict,
  useSourceConflictForDocument,
} from '../../hooks/use-source-conflicts'

const confirmationCopy: Record<SourceConflictResolution, string> = {
  keep_source: 'AgentWiki local content will be overwritten by the exact pending source snapshot.',
  keep_local:
    'This acknowledges only this exact pending revision. It does not change upstream source state, and newer revisions may conflict again.',
}

function formatReason(reason: string) {
  if (reason === 'manual_edit') return 'Manual edit detected'
  if (reason === 'local_override') return 'Local override detected'
  return 'Source and AgentWiki content differ'
}

function formatTimestamp(value: number) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

export function SourceConflictResolutionPanel({ documentId }: { documentId: string }) {
  const [decision, setDecision] = useState<SourceConflictResolution | null>(null)
  const { source, conflict, conflictId, isLoading, error, refetch } =
    useSourceConflictForDocument(documentId)
  const resolution = useResolveSourceConflict(documentId, conflictId)

  if (resolution.isSuccess) {
    return (
      <section className="aw-source-conflict-panel is-success" aria-live="polite">
        <CheckCircle2 className="h-5 w-5" aria-hidden />
        <div>
          <h2>
            {resolution.data.decision === 'keep_source'
              ? 'Source snapshot kept'
              : 'AgentWiki version kept'}
          </h2>
          <p>The conflict was resolved. Document and source status are refreshing.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="aw-source-conflict-panel" aria-labelledby="source-conflict-title">
      <div className="aw-source-conflict-heading">
        <AlertTriangle className="h-5 w-5" aria-hidden />
        <div>
          <span className="aw-label">Admin action</span>
          <h2 id="source-conflict-title">Resolve source conflict</h2>
          <p>Choose which version this read-only Source-backed document should keep.</p>
        </div>
      </div>

      {isLoading && (
        <p className="aw-source-conflict-state" aria-live="polite">
          Loading conflict details…
        </p>
      )}

      {!isLoading && error && (
        <div className="aw-source-conflict-state is-error" role="alert">
          <p>Conflict details could not be loaded. No content was changed.</p>
          <button type="button" className="aw-button sm" onClick={() => void refetch()}>
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden />
            Try again
          </button>
        </div>
      )}

      {!isLoading && !error && (!source?.conflict || !conflict || conflict.state !== 'pending') && (
        <p className="aw-source-conflict-state">
          This pending conflict is no longer available. Refresh the document before acting.
        </p>
      )}

      {!isLoading && !error && source?.conflict && conflict && conflict.state === 'pending' && (
        <>
          <dl className="aw-source-conflict-metadata">
            <div>
              <dt>Reason</dt>
              <dd>{formatReason(conflict.reason)}</dd>
            </div>
            <div>
              <dt>Detected</dt>
              <dd>{formatTimestamp(conflict.detectedAt)}</dd>
            </div>
            <div>
              <dt>Pending until</dt>
              <dd>{formatTimestamp(conflict.pendingExpiresAt)}</dd>
            </div>
          </dl>

          <div className="aw-source-conflict-actions">
            <button
              type="button"
              className="aw-button"
              onClick={() => {
                resolution.reset()
                setDecision('keep_local')
              }}
            >
              Keep AgentWiki version
            </button>
            <button
              type="button"
              className="aw-button primary"
              onClick={() => {
                resolution.reset()
                setDecision('keep_source')
              }}
            >
              Keep source version
            </button>
          </div>

          {decision && (
            <div
              className="aw-source-conflict-confirm"
              role="group"
              aria-label="Confirm resolution"
            >
              <strong>Confirm this irreversible choice</strong>
              <p>{confirmationCopy[decision]}</p>
              {resolution.isError && (
                <p className="aw-source-conflict-error" role="alert">
                  {sourceConflictErrorMessage(resolution.error)}
                </p>
              )}
              <div>
                <button
                  type="button"
                  className="aw-button ghost sm"
                  disabled={resolution.isPending}
                  onClick={() => setDecision(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aw-button primary sm"
                  disabled={resolution.isPending}
                  onClick={() => resolution.mutate(decision)}
                >
                  {resolution.isPending
                    ? 'Resolving…'
                    : decision === 'keep_source'
                      ? 'Confirm keep source'
                      : 'Confirm keep AgentWiki'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
